import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, type ReactNode } from 'react'
import { createRoot, hydrateRoot, type Root } from 'react-dom/client'
import { ErrorOverlay } from '@/components/ui/ErrorOverlay'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { LanguagesProvider } from '@/contexts/LanguagesContext'
import { SourceBadgesProvider } from '@/contexts/SourceBadgesContext'

const ROOT_KEY = '__inertiaRoot'
const OVERLAY_KEY = '__overlayRoot'

function InertiaProviders({ children }: { children: ReactNode }) {
  return (
    <LanguagesProvider>
      <SourceBadgesProvider>{children}</SourceBadgesProvider>
    </LanguagesProvider>
  )
}

void createInertiaApp({
  title: (title) => title ? `${title} — Mímir Languages` : 'Mímir Languages',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    page.default.layout ||= (children: ReactNode) => (
      <InertiaProviders>{children}</InertiaProviders>
    )

    return page
  },

  setup({ el, App, props }) {
    const typedEl = el as HTMLElement & { [ROOT_KEY]?: Root }
    const typedWindow = window as Window & { [OVERLAY_KEY]?: Root }

    const appJsx = (
      <StrictMode>
        <ThemeProvider>
          <App {...props} />
        </ThemeProvider>
      </StrictMode>
    )

    let root: Root
    if (typedEl[ROOT_KEY]) {
      root = typedEl[ROOT_KEY]
      root.render(appJsx)
    } else if (el.childElementCount > 0) {
      root = hydrateRoot(el, appJsx)
    } else {
      root = createRoot(el)
      root.render(appJsx)
    }
    typedEl[ROOT_KEY] = root

    if (!typedWindow[OVERLAY_KEY]) {
      const overlayEl = document.createElement('div')
      document.body.appendChild(overlayEl)
      typedWindow[OVERLAY_KEY] = createRoot(overlayEl)
    }

    router.on('invalid', (event) => {
      event.preventDefault()
      const status = (event.detail as { response?: { status?: number } }).response?.status ?? 500
      typedWindow[OVERLAY_KEY]!.render(
        <ErrorOverlay
          status={status}
          onDismiss={() => typedWindow[OVERLAY_KEY]!.render(null)}
        />
      )
    })
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: false,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
